"use client";

import { AnimatePresence, motion, useIsPresent, useReducedMotion, type Variants } from "motion/react";
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

import {
  TB_DURATION_MS,
  TB_EASE_QUILL,
  TB_RISE_DISTANCE_PX,
} from "@/lib/motion/motion-tokens";
import { scrollPageToTop } from "@/lib/motion/page-transition";

const VARIANTS: Variants = {
  initial: { opacity: 0, y: TB_RISE_DISTANCE_PX },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -TB_RISE_DISTANCE_PX },
};

const TRANSITION = {
  duration: TB_DURATION_MS[3] / 1000,
  ease: TB_EASE_QUILL,
};

/** Under `prefers-reduced-motion: reduce` the step swaps in place (handoff v2 §14: 0,001ms). */
const REDUCED_VARIANTS: Variants = {
  initial: { opacity: 1, y: 0 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 1, y: 0 },
};

const REDUCED_TRANSITION = { duration: 0 };

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Moves the page to `top` (a document offset) by the project's scroll foundation (Motion Guide
 * §0): through the shared Lenis when one runs, never the browser's native smooth scroll; a jump
 * when there is no Lenis or when reduced motion is asked for.
 */
function scrollPageTo(top: number): void {
  const lenis = window.__tbLenis;
  if (lenis !== undefined && !prefersReducedMotion()) {
    lenis.scrollTo(top);
    return;
  }
  if (top <= 0) {
    scrollPageToTop();
    return;
  }
  lenis?.scrollTo(top, { immediate: true, force: true });
  window.scrollTo({ top, left: 0, behavior: "instant" });
}

/** Duration of the exit animation (ms) — scroll is delayed until after exit completes. */
const EXIT_DURATION_MS = TB_DURATION_MS[3];

/**
 * Lands the page where the step asks: `"top"` (default), the middle of the window over a CSS
 * selector's element, or nowhere for `"none"`. A selector that matches nothing lands on top.
 */
function repositionPage(target: string | undefined): void {
  if (target === "none") return;

  const element = !target || target === "top" ? null : document.querySelector(target);
  if (element === null) {
    scrollPageTo(0);
    return;
  }

  // The target lands in the middle of the window, as `scrollIntoView({ block: "center" })` did.
  const rect = element.getBoundingClientRect();
  scrollPageTo(Math.max(0, window.scrollY + rect.top - (window.innerHeight - rect.height) / 2));
}

interface ScreenTransitionProps {
  screenKey: string;
  children: ReactNode;
  /** Where to land after the step change: `"top"` (default), a CSS selector, or `"none"`. */
  scrollTarget?: string;
  /**
   * The step on screen leaves (fade and 14px, 360ms) before the next one arrives, and the page
   * is repositioned when it does (Onboarding V2 §10: one screen at a time).
   *
   * Opt-in, and off by default, because the flows that share this component and are not ours
   * (the store's assisted creation, the checkout) keep their current behaviour: the step
   * swapped at once. Without it, the next step faded in at the old scroll position and the page
   * jumped up 360ms later — on the Dados form, scrolled to its button, the address form's empty
   * fields showed where the filled ones had been (measured on video, 2026-10-26).
   */
  animateExit?: boolean;
  /**
   * The step that is on screen when this mounts **arrives already in place**: no fade, no rise.
   * Later steps keep their animation.
   *
   * Opt-in, and off by default, for the same reason as `animateExit` — the admin's own flow shares
   * this component and keeps what it shows. Without it the first step left the server at
   * `opacity: 0` and waited for script to hydrate and run the 360ms entrance, so the onboarding's
   * first paint (and its LCP) came late: 6.1s on a phone (PERF-04). The server's HTML is the
   * finished step.
   */
  skipFirstEntrance?: boolean;
}

function Screen({
  screenKey,
  children,
  repositionOnEnter,
  scrollTarget,
}: {
  screenKey: string;
  children: ReactNode;
  /** Reposition the page when this step mounts: it only mounts once the previous one has left. */
  repositionOnEnter: boolean;
  /** The step's own target, as it was when the step was asked for. */
  scrollTarget?: string;
}) {
  const reducedMotion = useReducedMotion() === true;
  // False while this step is leaving (only ever, under `animateExit`).
  const isPresent = useIsPresent();

  // Mount only: the step's arrival, once the previous one has left, so a target inside it
  // (the e-mail field, back from the address) is already in the document.
  useEffect(() => {
    if (repositionOnEnter) repositionPage(scrollTarget);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    // `min-w-0 max-w-full`: as a flex item of a centring row this wrapper would otherwise never
    // go narrower than its content's minimum width, and a wide step would run off a phone.
    //
    // A leaving step is inert and hidden from assistive technology: for its 360ms it is still on
    // screen, and a second press of its action (the signup's "Criar cadastro") would fire again.
    <motion.div
      key={screenKey}
      inert={!isPresent}
      aria-hidden={isPresent ? undefined : true}
      className="min-w-0 max-w-full"
      variants={reducedMotion ? REDUCED_VARIANTS : VARIANTS}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={reducedMotion ? REDUCED_TRANSITION : TRANSITION}
    >
      {children}
    </motion.div>
  );
}

/**
 * Wraps the wizard's current step so a step change replays the DS exit-before-enter and
 * repositions the page.
 */
export function ScreenTransition({
  screenKey,
  children,
  scrollTarget,
  animateExit = false,
  skipFirstEntrance = false,
}: ScreenTransitionProps) {
  // Read at firing time out of a ref: an error clearing on the same step must not schedule
  // another scroll.
  const scrollTargetRef = useRef(scrollTarget);
  useEffect(() => {
    scrollTargetRef.current = scrollTarget;
  });

  useEffect(() => {
    // With the exit animated, the arriving step repositions the page itself (`Screen`).
    if (animateExit) return;

    const target = scrollTargetRef.current;
    if (target === "none") return;

    // Waits out the exit, so the scroll and the motion do not fight.
    const timer = setTimeout(() => repositionPage(target), prefersReducedMotion() ? 0 : EXIT_DURATION_MS);

    return () => clearTimeout(timer);
  }, [screenKey]); // eslint-disable-line react-hooks/exhaustive-deps -- step change only: `scrollTarget` here would rescroll on its own.

  return (
    // `initial={false}` is Motion's own switch for "the children present at first render skip their
    // entrance"; a step that arrives later still animates.
    <AnimatePresence mode="wait" initial={!skipFirstEntrance}>
      {/*
        The key is what lets `AnimatePresence` see a step leave. On the inner `motion.div` alone
        it only made React remount it, and nothing ever exited.
      */}
      <Screen
        key={animateExit ? screenKey : undefined}
        screenKey={screenKey}
        repositionOnEnter={animateExit}
        scrollTarget={scrollTarget}
      >
        {children}
      </Screen>
    </AnimatePresence>
  );
}
